import { useEffect } from "react";
import classNames from "classnames";
import { ReactComponentPropsBase } from "../../../base-props/ReactComponentPropsBase";
import "./RichText.scss";
import styles from "./RichText.module.scss";
import useSelectTheme from "../../../utils/useSelectTheme";
import IconMS from "../../utils/IconMS/IconMS";
import { createRoot } from "react-dom/client";

export interface RichTextProps extends ReactComponentPropsBase {
  html: string;
  className?: string;
  style?: React.CSSProperties;
  textRefFunction?: (element: HTMLDivElement) => void;
}

const RichText: React.FC<RichTextProps> = ({
  html,
  className,
  style,
  textRefFunction,
}) => {
  const selectedTheme = useSelectTheme("", "mangfoldsprisLayout");

  // Function to add icons to external links
  const addExternalLinkIcons = (container: HTMLDivElement | null) => {
    if (!container) return;

    const links = container.querySelectorAll<HTMLAnchorElement>("a");

    links.forEach(link => {
      if (link.target === "_blank" && link.classList.contains("externalLink")) {
        return;
      }

      if (link.target === "_blank") {
        link.classList.add("externalLink");

        const iconContainer = document.createElement("span");

        const iconElement = (
          <IconMS className={styles.linkIcon} name="open_in_new" size="16px" />
        );

        const root = createRoot(iconContainer);
        root.render(iconElement);

        link.appendChild(iconContainer);
      }
    });
  };

  // Function to make iframes responsive with aspect ratio
  const makeIframesResponsive = (container: HTMLDivElement | null) => {
    if (!container) return;

    const iframes = container.querySelectorAll<HTMLIFrameElement>("iframe");

    iframes.forEach(iframe => {
      const src = iframe.getAttribute("src") || "";

      // Skip YouTube and Vimeo embeds
      if (/youtube\.com|vimeo\.com/.test(src)) {
        return;
      }

      const widthAttr = iframe.getAttribute("width");
      const heightAttr = iframe.getAttribute("height");

      let aspectRatio = 4 / 3; // default ratio
      if (widthAttr && heightAttr) {
        const width = parseInt(widthAttr, 10);
        const height = parseInt(heightAttr, 10);
        if (width > 0 && height > 0) {
          aspectRatio = width / height;
        }
      }

      // Skip if iframe already wrapped
      if (iframe.parentElement?.classList.contains(styles.iframeWrapper)) {
        return;
      }

      const wrapper = document.createElement("div");
      wrapper.classList.add(styles.iframeWrapper);
      wrapper.style.position = "relative";
      wrapper.style.width = "100%";
      wrapper.style.paddingTop = `${100 / aspectRatio}%`;

      iframe.style.position = "absolute";
      iframe.style.top = "0";
      iframe.style.left = "0";
      iframe.style.width = "100%";
      iframe.style.height = "100%";

      iframe.parentNode?.insertBefore(wrapper, iframe);
      wrapper.appendChild(iframe);
    });
  };

  useEffect(() => {
    const containers = document.querySelectorAll<HTMLDivElement>(
      `.${styles.richText}`
    );

    containers.forEach(container => {
      addExternalLinkIcons(container);
      makeIframesResponsive(container);
    });
  }, []);

  return (
    <div
      className={classNames(styles.richText, className, styles[selectedTheme])}
      dangerouslySetInnerHTML={{ __html: html }}
      ref={textRefFunction}
      style={style}
    />
  );
};

export default RichText;
